import configEntzy from "components/config/ConfigEntzy";

/**
 * Identity User Item Object Definition
 * @typedef {Object} IdentityUserItem
 * @property {number} stateid - The state id of the user object, used for tracking updates
 * @property {boolean} connected - Whether the user is connected or not
 * @property {boolean} admin - Whether the user is an admin or not
 * @property {string} provider - The identity provider of the user (e.g. "internal", "google", "facebook")
 * @property {string} mfa - The MFA status of the user (e.g. "NOMFA", "TOTP")
 * @property {string} identity - The identity uuid of the user
 * @property {string} name - The name of the user
 * @property {string} username - The cognito username NOTE: not used in app but stored as sub
 * @property {string} sub - The cognito subject identifier
 * @property {string} email - The email of the user
 * @property {string|null} notifications - JSON stringified top level notification settings to mute all notifications e.g. DMs or events
 * @property {Object} avatar - The avatar of the user
 * @property {string|null} avatar.key - The S3 key of the avatar
 * @property {Object} avatar.images - The different sizes of the avatar
 * @property {boolean} avatar.hydrated - Whether the avatar has been hydrated with S3 URLs
 * @property {Object} tribe - The tribe of the user
 * @property {Object} status - The status of the user
 * @property {string|null} status.last_connect - The last time the user connected to the app
 * @property {string|null} status.device_synced - The last time the user synced their device
 * @property {string|null} status.last_email_verified_address - The last email address the user verified
 * @property {string|null} status.last_email_verified_timestamp - The last time the user verified their email
 * @property {boolean} status.last_email_verified - Whether the user's last email was verified
 * @property {boolean} status.verified - Whether the user is verified
 * @property {boolean} status.reset - Whether the user has not completed email verification and needs to reset
 * @property {boolean} status.payment_set - Whether the user has set payment card details
 * @property {boolean} status.payout_set - Whether the user has set payout bank details *
 */

/**
 * Create Identity User Item Object
 * @param {Object} params
 * @param {number} [params.stateid=Date.now()]
 * @param {boolean} [params.connected=false]
 * @param {boolean} [params.admin=false]
 * @param {string} [params.mfa="NOMFA"]
 * @param {string} [params.provider="internal"]
 * @param {string} [params.identity="guest:user:id:" + Date.now()]
 * @param {string} [params.sub="guest:user:sub:" + Date.now()]
 * @param {string} [params.username="guest:user:sub:" + Date.now()]
 * @param {string} [params.name="guest"]
 * @param {string} [params.email="guest@entzy.com"]
 * @param {string|null} [params.notifications=null]
 * @param {Object} [params.avatar={}]
 * @param {string|null} [params.avatar.key=null]
 * @param {Object} [params.avatar.images={}]
 * @param {boolean} [params.avatar.hydrated=false]
 * @param {Object} [params.tribe={}]
 * @param {Object} [params.status={}]
 * @param {string|null} [params.status.last_connect=null]
 * @param {string|null} [params.status.device_synced=null]
 * @param {string|null} [params.status.last_email_verified_address=null]
 * @param {string|null} [params.status.last_email_verified_timestamp=null]
 * @param {boolean} [params.status.last_email_verified=false]
 * @param {boolean} [params.status.verified=false]
 * @param {boolean} [params.status.reset=false]
 * @param {boolean} [params.status.payment_set=false]
 * @param {boolean} [params.status.payout_set=false]
 * @returns {IdentityUserItem}
 */
export function createIdentityUserItem({
  stateid = Date.now(),
  connected = false,
  admin = false,
  mfa = "NOMFA",
  provider = "internal",
  identity = "guest:user:id:" + Date.now(),
  sub = "guest:user:sub:" + Date.now(),
  username = "guest:user:sub:" + Date.now(),
  name = "guest",
  email = "guest@entzy.com",
  notifications = null,
  avatar = {
    key: null,
    images: {},
    hydrated: false,
  },
  tribe = {},
  status = {
    last_connect: null,
    device_synced: null,
    last_email_verified_address: null,
    last_email_verified_timestamp: null,
    last_email_verified: false,
    verified: false,
    reset: false,
    payment_set: false,
    payout_set: false,
  },
}) {
  return {
    stateid,
    connected,
    admin,
    mfa,
    provider,
    identity,
    sub,
    username,
    name,
    email,
    notifications,
    avatar,
    tribe,
    status,
  };
}

/**
 * Identity User Setting Item Object Definition
 * @typedef {Object} IdentityUserSettingItem
 * @property {Object} data - The user setting data object
 * @property {string} data.UserId - The user id of the settings
 * @property {string} data.Name - The name of the user
 * @property {string|null} data.Email - The email of the user
 * @property {string|null} data.IdentityProvider - The identity provider of the user (e.g. "internal", "google", "facebook")
 * @property {string|null} data.FirstCreated - The timestamp of when the user first created their account
 * @property {string|null} data.LastConnect - The timestamp of when the user last connected to the app
 * @property {string|null} data.LastUpdated - The timestamp of when the user last updated their settings
 * @property {string} data.Avatar - The S3 key of the user's avatar image
 * @property {string} data.PaymentId - The payment method id of the user
 * @property {string} data.PayoutId - The payout method id of the user
 * @property {string|null} activePaymentMethod - The active payment method of the user
 * @property {boolean} hydrated - Whether the settings have been hydrated with user data
 */

/**
 * Create Identity User Setting Item Object
 * @returns {IdentityUserSettingItem}
 */
export function createIdentityUserSettingItem() {
  return {
    data: {
      UserId: "guest:user:id:" + Date.now(),
      Name: "guest",
      Email: null,
      IdentityProvider: null,
      FirstCreated: null,
      LastConnect: null,
      LastUpdated: null,
      Avatar: configEntzy.EMPTY_STRING_SET,
      PaymentId: configEntzy.EMPTY_STRING_SET,
      PayoutId: configEntzy.EMPTY_STRING_SET,
    },
    activePaymentMethod: null,
    hydrated: false,
  };
}
